import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Stack,
  Avatar,
  Chip,
  Button
} from '@mui/material';
import { Business, People, TrendingUp, Security, Star, CheckCircle } from '@mui/icons-material';

const About = () => {
  const values = [
    {
      icon: <Star sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Excellence',
      description: 'We are committed to delivering the highest quality solutions, ensuring every project meets our rigorous standards for performance and reliability.'
    },
    {
      icon: <People sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Collaboration',
      description: 'We work closely with our clients as trusted partners, understanding their unique challenges and collaborating to achieve their business objectives.'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Innovation',
      description: 'We constantly push the boundaries of what\'s possible, leveraging the latest technologies to create solutions that exceed expectations.'
    }
  ];

  const achievements = [
    '500+ Successful Projects',
    '50+ Expert Consultants',
    '10+ Years of Experience',
    '99% Client Satisfaction Rate',
    '24/7 Support Available',
    'Global Presence'
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 10,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            About Scherstellen Technologie
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            We are a leading technology company dedicated to transforming businesses through innovative solutions and cutting-edge expertise.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">

        {/* Mission Section */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ color: '#167d7f', mb: 3, fontWeight: 'bold' }}>
                Our Mission
              </Typography>
              <Typography variant="h6" color="text.secondary" paragraph>
                To empower businesses with technology solutions that drive growth, efficiency, and innovation. 
                We believe in the transformative power of technology to solve complex challenges and create 
                opportunities for our clients.
              </Typography>
              <Typography variant="h6" color="text.secondary" paragraph>
                Our obsession is making brands stand out, and we have the expertise to do just that. 
                Trust us to elevate your brand and make it truly memorable.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 2 }}>
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#167d7f', mr: 2 }}>
                      <Business />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Industry Expertise
                      </Typography>
                      <Typography color="text.secondary">
                        Deep knowledge across multiple industries
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#167d7f', mr: 2 }}>
                      <People />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Expert Team
                      </Typography>
                      <Typography color="text.secondary">
                        Highly skilled professionals
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#167d7f', mr: 2 }}>
                      <TrendingUp />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Proven Results
                      </Typography>
                      <Typography color="text.secondary">
                        Track record of successful projects
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#167d7f', mr: 2 }}>
                      <Security />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Security First
                      </Typography>
                      <Typography color="text.secondary">
                        Enterprise-grade security standards
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Values Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: '#167d7f', mb: 6, textAlign: 'center', fontWeight: 'bold' }}>
            Our Values
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'white', height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: '#167d7f', mb: 2, fontWeight: 'bold' }}>
                    Innovation
                  </Typography>
                  <Typography color="text.secondary">
                    We constantly push the boundaries of what's possible, leveraging the latest technologies 
                    to create solutions that exceed expectations.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'white', height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: '#167d7f', mb: 2, fontWeight: 'bold' }}>
                    Excellence
                  </Typography>
                  <Typography color="text.secondary">
                    We are committed to delivering the highest quality solutions, ensuring every project 
                    meets our rigorous standards for performance and reliability.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'white', height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: '#167d7f', mb: 2, fontWeight: 'bold' }}>
                    Partnership
                  </Typography>
                  <Typography color="text.secondary">
                    We work closely with our clients as trusted partners, understanding their unique 
                    challenges and collaborating to achieve their business objectives.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Stats Section */}
        <Box sx={{ bgcolor: '#167d7f', color: 'white', borderRadius: 2, p: 6 }}>
          <Typography variant="h3" sx={{ mb: 6, textAlign: 'center', fontWeight: 'bold' }}>
            By the Numbers
          </Typography>
          
          <Grid container spacing={4} sx={{ textAlign: 'center' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                500+
              </Typography>
              <Typography variant="h6">
                Projects Completed
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                50+
              </Typography>
              <Typography variant="h6">
                Expert Team Members
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                10+
              </Typography>
              <Typography variant="h6">
                Years of Experience
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                99%
              </Typography>
              <Typography variant="h6">
                Client Satisfaction
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default About;
